import type { AuditEvent } from "@salesboard/contracts";

export type AuditGroup = {
  /** Stable key for rendering — the newest event's id. */
  key: string;
  signature: string;
  events: AuditEvent[];
};

/**
 * Collapse consecutive similar events for display.
 *
 * This is bd-admin's render-time grouping, and it is deliberately **not** bd-admin's
 * write-time coalescing. Coalescing there merges rows in the database and moves their
 * timestamps, which cannot coexist with an append-only log — and it loses the individual
 * before/after values, which for an accountability record is destroying the evidence the
 * log exists to hold.
 *
 * So every change stays its own row, and only the presentation collapses: a four-field
 * edit is four audit rows shown as one expandable line.
 *
 * Only *consecutive* runs group. An interleaved actor breaks the run, because "Hana made
 * four changes" and "Hana and Petr each made two" are different stories.
 *
 * The signature carries **all four** scopes. It listed two, which was complete when there
 * were two and silently stopped being so when stages and settings arrived: two renames of two
 * *different* stages by one actor have the same type and the same actor, and with no stage in
 * the signature they collapsed into one line claiming "renamed this stage ×2". Invisible on a
 * per-record timeline, where every row is the same record anyway — and wrong on the first
 * cross-record feed, which is the configuration history and now the activity feed.
 */
export function groupConsecutive(events: AuditEvent[]): AuditGroup[] {
  const groups: AuditGroup[] = [];

  for (const event of events) {
    const scopeId = event.opportunityId ?? event.userId ?? event.stageId ?? event.settingKey ?? "";
    const signature = `${event.eventType}|${event.actorId}|${scopeId}`;
    const last = groups.at(-1);

    if (last && last.signature === signature) last.events.push(event);
    else groups.push({ key: event.id, signature, events: [event] });
  }

  return groups;
}

/**
 * The values a group spans: the oldest event's `oldValue` and the newest one's
 * `newValue`, so a run of edits reads as one journey rather than the last hop.
 *
 * Events arrive newest-first, which is why the ends look reversed here.
 */
export function groupValueRange(group: AuditGroup): { oldValue: string | null; newValue: string | null } {
  const newest = group.events[0];
  const oldest = group.events.at(-1);
  return {
    oldValue: oldest?.oldValue ?? null,
    newValue: newest?.newValue ?? null,
  };
}
